<template>
  <div class="login-page d-flex justify-content-center align-items-center">
    <transition name="fade-slide">
      <form
        v-show="mounted"
        class="login-card shadow-lg p-4 p-md-5"
        @submit.prevent="handleLogin"
      >
        <div class="text-center mb-4">
          <i class="fa-solid fa-circle-nodes fa-2x text-primary mb-2" />
          <h1 class="h4 fw-semibold mb-0">Вход в систему</h1>
          <p v-if="registeredHint" class="text-success small mt-2 mb-0">{{ registeredHint }}</p>
        </div>

        <div class="mb-3 input-group">
          <span class="input-group-text bg-white">
            <i class="fa-regular fa-user" />
          </span>
          <input
            v-model="identifier"
            type="text"
            class="form-control"
            placeholder="E-mail или логин"
            autocomplete="username"
            required
          />
        </div>

        <div class="mb-2 input-group password-group">
          <span class="input-group-text bg-white">
            <i class="fa-solid fa-lock" />
          </span>
          <input
            v-model="password"
            :type="showPwd ? 'text' : 'password'"
            class="form-control"
            placeholder="Пароль"
            autocomplete="current-password"
            required
          />
          <button
            type="button"
            class="btn btn-outline-secondary border-start-0"
            @click="showPwd = !showPwd"
            :title="showPwd ? 'Скрыть' : 'Показать'"
          >
            <i :class="showPwd ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'" />
          </button>
        </div>

        <p class="text-end small mb-3">
          <router-link to="/forgot-password">Забыли пароль?</router-link>
        </p>

        <div v-if="showResendBlock" class="alert alert-warning py-2 small mb-3">
          <p class="mb-2">{{ resendHint }}</p>
          <button
            type="button"
            class="btn btn-sm btn-outline-dark"
            :disabled="resendLoading"
            @click="resendVerification"
          >
            <span v-if="resendLoading" class="spinner-border spinner-border-sm me-1" />
            Отправить письмо повторно
          </button>
          <p v-if="resendMessage" class="mb-0 mt-2 text-success">{{ resendMessage }}</p>
        </div>

        <button class="btn btn-primary w-100" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
          Войти
        </button>

        <p v-if="!hideRegistration" class="text-center small text-muted mt-3 mb-0">
          Нет аккаунта?
          <router-link to="/register">Зарегистрироваться</router-link>
        </p>
        <p v-else-if="inviteOnlyHint" class="text-center small text-muted mt-3 mb-0">
          {{ inviteOnlyHint }}
        </p>
      </form>
    </transition>

    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index:1080">
      <div
        ref="toastErr"
        class="toast align-items-center text-bg-danger border-0"
        role="alert"
      >
        <div class="d-flex">
          <div class="toast-body">{{ toastMsg }}</div>
          <button
            type="button"
            class="btn-close btn-close-white me-2 m-auto"
            data-bs-dismiss="toast"
            aria-label="Close"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/store/pinia";
import api from "@/api";

defineProps({
  hideRegistration: { type: Boolean, default: false },
});

const auth = useAuthStore();
const route = useRoute();

const inviteOnlyHint = computed(() =>
  route.query.invite === "only"
    ? "Доступ выдаётся по приглашению. Обратитесь к администратору."
    : ""
);

const registeredHint = computed(() =>
  route.query.registered === "1"
    ? "Проверьте почту и перейдите по ссылке для подтверждения регистрации."
    : ""
);

const identifier = ref("");
const password   = ref("");
const showPwd    = ref(false);
const loading    = ref(false);

const showResendBlock = ref(false);
const resendHint = ref("");
const resendEmail = ref("");
const resendLoading = ref(false);
const resendMessage = ref("");

const toastErr = ref(null);
const toastMsg = ref("");

function showError(msg) {
  toastMsg.value = msg;
  new window.bootstrap.Toast(toastErr.value).show();
}

async function resendVerification() {
  if (!resendEmail.value) return;
  resendLoading.value = true;
  resendMessage.value = "";
  try {
    const { data } = await api.post("/auth/resend-verification/", {
      email: resendEmail.value,
    });
    resendMessage.value = data?.detail || "Письмо отправлено, если аккаунт ожидает подтверждения.";
  } catch {
    resendMessage.value = "Не удалось отправить. Попробуйте позже.";
  } finally {
    resendLoading.value = false;
  }
}

async function handleLogin() {
  loading.value = true;
  showResendBlock.value = false;
  resendMessage.value = "";
  try {
    await auth.login(identifier.value.trim(), password.value);
  } catch (e) {
    if (e?.isEmailNotVerified) {
      resendHint.value = e.userMessage || "Подтвердите e-mail по ссылке из письма.";
      resendEmail.value = identifier.value.includes("@")
        ? identifier.value.trim()
        : "";
      showResendBlock.value = true;
      showError(resendHint.value);
    } else if (e?.isInactiveAccount) {
      showError("Аккаунт заблокирован. Обратитесь в поддержку.");
    } else {
      showError("Неверный e-mail / логин или пароль");
    }
  } finally {
    loading.value = false;
  }
}

const mounted = ref(false);
onMounted(() => (mounted.value = true));
</script>

<style scoped>

.login-page {
  min-height: 100vh;    
  padding: 1.5rem;
}


.login-card {
  width: 100%;
  max-width: 480px;               
  border: none;
  border-radius: 1rem;
  background: #ffffff;             
}


.fade-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(18px);
}


.input-group > .input-group-text {
  border-right: 0;
  background: transparent;
  padding-inline: 0.75rem;
}
.password-group .btn {
  border-left: 0;
}
.password-group .btn:focus {
  box-shadow: none;
}
</style>
